import { get, compact } from 'lodash';
import { flow, last, sortBy } from 'lodash/fp';
import moment from 'moment';
import { messageFormat } from '@clubmed/utils';
import type { IconName } from 'Components/frameworks/icon/Icon';
import 'Components/frameworks/icon/Icon';
import { tAlt } from 'Components/utils/booking/bookingUtils';
import { ICONS } from 'Components/utils/icon/icons';
import type { Props as Accommodationprops } from 'Components/widgets/accommodation/Accommodation';
import 'Components/widgets/accommodation/Accommodation';
import type { CurrentComfortAlternativesType } from 'Components/widgets/accommodation-room/infos/AccommodationRoomInfos.utils';
import { hasTypedAlternative } from 'Components/widgets/accommodation-room/infos/AccommodationRoomInfos.utils';
import { t } from 'Src/i18n';
import {
  ACCOMMODATION_COMFORT_ORDER,
  CORRESPONDANCE_ROOM_TYPE,
  CORRESPONDENCE_CABIN_TYPE,
} from 'Utils/accommodation/accommodation.constants';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import { asProposalFixture } from 'Src/types/view-helpers';
import {
  compareAccommodationsByComfort,
  isPremiumAccommodation,
  isExclusiveCollectionRoom,
  isExclusiveCollectionSpace,
} from 'Utils/proposal/proposalUtils';
type LabeledIcon = {
  icon: IconName;
  labels: Array<string>;
  buttonText?: string;
};
const ATTENDEE_CATEGORIES = {
  BABY: 'BABY',
  CHILD: 'CHILD',
  ADULT: 'ADULT',
};
const ICONS_ROOM_TYPE = {
  CONNECTING_ROOMS: ICONS.CONNECTING_ROOMS,
  SEPARATE_ROOMS_3: ICONS.SEPARATE_ROOMS_3,
  SEPARATE_ROOMS: ICONS.SEPARATE_ROOMS,
  SINGLE_ROOM: ICONS.SINGLE_ROOM,
};

function getCapacityIconItem(capacity?: number | null): LabeledIcon | null | undefined {
  const label = t('booking.accommodation.packageContent.numberPeople');

  if (!capacity || !label) {
    return null;
  }

  return {
    icon: ICONS.PEOPLE,
    labels: [
      t('booking.accommodation.packageContent.capacityOfPeople'),
      messageFormat(label).format({
        count: capacity,
      }),
    ],
  };
}

function getSurfaceIconItem(
  roomType: string,
  product: Product,
  surface: string | null | undefined,
): LabeledIcon {
  return {
    icon: ICONS_ROOM_TYPE[roomType] || null,
    labels: [
      tAlt({
        cruiseKeyPath: `booking.accommodation.packageContent.configuration.${CORRESPONDENCE_CABIN_TYPE[roomType]}`,
        product,
        resortKeyPath: `booking.accommodation.packageContent.configuration.${CORRESPONDANCE_ROOM_TYPE[roomType]}`,
      }),
      surface || (roomType && ' ') || '',
    ],
  };
}

function getNbBabies(occupancies: Array<Occupancy> | null | undefined): number {
  if (!occupancies) {
    return 0;
  }

  return occupancies.reduce(
    (acc: number = 0, occupancy: Occupancy) => acc + occupancy.baby_count,
    0,
  );
}

function getBabyBedIconItem(babyCount?: number): LabeledIcon | null | undefined {
  if (!babyCount || babyCount <= 0) {
    return null;
  }

  const label =
    babyCount > 1
      ? t('booking.accommodation.packageContent.babyBedincludedPlural')
      : t('booking.accommodation.packageContent.babyBedincluded');
  return {
    icon: ICONS.baby_bed,
    labels: [label],
  };
}

function getOutsideFacilitiesIconItem(
  outsideFacilities?: Array<AccommodationEquipment> | null,
): LabeledIcon | null | undefined {
  return get(outsideFacilities, '[0].label')
    ? {
        icon: ICONS[get(outsideFacilities![0], 'id')] || ICONS.TRIDENT1,
        labels: [outsideFacilities![0].label],
      }
    : null;
}

function getViewIconItem(view?: AccommodationView | null): LabeledIcon | null | undefined {
  return view
    ? {
        icon: ICONS[get(view, 'id')] || ICONS.TRIDENT1,
        labels: [view.label],
      }
    : null;
}

function aggregateIconList(
  strengths: Array<AccommodationStrength> | null | undefined,
  surface: LabeledIcon,
  capacity?: LabeledIcon | null,
  outsideFacility?: LabeledIcon | null,
  view?: LabeledIcon | null,
  babyBed?: LabeledIcon | null,
  isComparator?: boolean | null,
  isInMoreRoomLayer?: boolean | null,
  proposal?: Proposal | null,
  currentComfortAlternatives?: CurrentComfortAlternativesType,
): Array<LabeledIcon> {
  // add a "strengths slot" for each missing item
  const maxStrengths =
    3 - [view, capacity, babyBed, outsideFacility].filter((item) => get(item, 'labels.0')).length;
  const strengthsItems = (strengths || []).slice(0, Math.max(maxStrengths, 0)).map((strength) => ({
    icon: ICONS[get(strength, 'id')] || ICONS.TRIDENT1,
    labels: [get(strength, 'label') || ''],
  }));
  const chooseYourViewItem = {
    icon: ICONS.sea_view,
    labels: [t('booking.accommodation.roomFinder.viewFilterTitle')],
    buttonText: t('booking.common.add'),
  };
  const chooseYourFacilityItem = {
    icon: 'terrace',
    labels: [t('booking.accommodation.roomFinder.facilitiesFilterTitle')],
    buttonText: t('booking.common.add'),
  };
  const hasViewAlternative = hasTypedAlternative({
    currentComfortAlternatives,
    icon: ICONS.sea_view,
    proposal,
  });
  const hasOFAlternative = hasTypedAlternative({
    currentComfortAlternatives,
    icon: 'terrace',
    proposal,
  });
  const viewWithDefaultIfNone =
    !isComparator && !isInMoreRoomLayer && hasViewAlternative ? view || chooseYourViewItem : view;
  const outsideFacilityWithDefaultIfNone =
    !isComparator && !isInMoreRoomLayer && hasOFAlternative
      ? outsideFacility || chooseYourFacilityItem
      : outsideFacility;
  return compact([
    surface,
    capacity,
    outsideFacilityWithDefaultIfNone,
    babyBed,
    ...strengthsItems,
    viewWithDefaultIfNone,
  ]).filter(({ labels = [] }) => labels.length && labels[0]);
}

function hasLowStock(threshold: number, stock: number) {
  return stock <= threshold;
}

function getAccommodationCategoryComfortIndex(category: category): number {
  return ACCOMMODATION_COMFORT_ORDER.indexOf(category.id);
}

function getBestAccommodationCategory(categories: Array<category>): category {
  if (categories.length === 1) {
    return categories[0];
  }

  return flow(sortBy(getAccommodationCategoryComfortIndex), last)(categories) as category;
}

const getAccommodationRoomsProps = ({
  accommodations = [],
  accommodationsCount,
  attendeesWithDistributionData,
  proposal = asProposalFixture({}),
  remainingStock,
  product,
  streetViews,
  isComparator = false,
  isInMoreRoomLayer = false,
  alternatives,
}: {
  accommodations: Array<Accommodation>;
  accommodationsCount: number | null | undefined;
  attendeesWithDistributionData: Array<any>;
  proposal: Proposal | null | undefined;
  remainingStock: number | null | undefined;
  product: Product;
  streetViews?: Array<StreetViewWithAccommodationCategoryId>;
  isComparator?: boolean;
  isInMoreRoomLayer?: boolean;
  alternatives?: any;
}) => {
  const sortedAccommodations = [...accommodations]
    .sort(compareAccommodationsByComfort)
    .filter(Boolean);
  const sortedAttendees = {
    adults: attendeesWithDistributionData.filter(({ type }) => type === ATTENDEE_CATEGORIES.ADULT),
    babies: attendeesWithDistributionData.filter(({ type }) => type === ATTENDEE_CATEGORIES.BABY),
    children: attendeesWithDistributionData.filter(
      ({ type }) => type === ATTENDEE_CATEGORIES.CHILD,
    ),
  };
  return sortedAccommodations.map((a) => {
    const attendeesIds = (a.attendeesDistribution?.attendees || []).map((at) => at.id);
    const matchingStreetViewElement = streetViews?.length
      ? streetViews.find(({ accommodationCategoryId }) => accommodationCategoryId === a.categoryId)
      : {};
    const isPremium = isPremiumAccommodation(a);
    const isExclusiveCollection = isExclusiveCollectionRoom(a, product);

    const getSplitServices = (
      services: Array<AccommodationEquipment>,
    ): Array<Array<AccommodationEquipment>> => {
      const servicesToSplit = services.slice(0);

      if (isExclusiveCollection) {
        const mainServices = servicesToSplit.splice(0, 6);
        return [servicesToSplit, mainServices];
      }

      return [servicesToSplit];
    };

    const services = a.services && getSplitServices(a.services);

    const getServicesIconsList = () => {
      if (services?.[1]?.length > 0) {
        return services[1].map(({ id, label }) => ({
          icon: ICONS[id] || ICONS.TRIDENT1,
          labels: [label],
        }));
      }

      return null;
    };

    const currentComfortAlternatives = alternatives?.find(({ categoryId }) => {
      return categoryId === a.categoryId;
    });
    return {
      bathroomEquipments: a.bathroomEquipments,
      categoryId: a.categoryId,
      description: a.description,
      dates: {
        endDate: moment(a.endDate, API_DATE_FORMAT).valueOf(),
        startDate: moment(a.startDate, API_DATE_FORMAT).valueOf(),
      },
      distribution:
        (sortedAccommodations.length > 1 || a.quantity > 1) && !!attendeesIds.length
          ? {
              adults: sortedAttendees.adults.filter((at) => attendeesIds.includes(at.id)),
              babies: sortedAttendees.babies.filter((at) => attendeesIds.includes(at.id)),
              children: sortedAttendees.children.filter((at) => attendeesIds.includes(at.id)),
            }
          : null,
      equipments: a.equipments,
      hasArrangement:
        accommodations.length !==
        (accommodationsCount ? accommodationsCount : proposal?.accommodationsCount),
      hasDistribution: sortedAccommodations.length > 1 && !!attendeesIds.length,
      hasSharedRoom:
        (proposal?.attendees?.adults?.length || 0) +
          (proposal?.attendees?.children?.length || 0) ===
          1 && product.id === 'KEMC',
      id: a.id,
      images: (a.media?.photos || []).map((m) => ({
        src: m.url,
      })),
      floorPlans: a.floorPlans,
      name: a.label,
      infosList: aggregateIconList(
        a.strengths,
        getSurfaceIconItem(a.roomType, product, a.surface),
        undefined,
        getOutsideFacilitiesIconItem(a.outsideFacilities),
        getViewIconItem(a.view),
        getBabyBedIconItem(getNbBabies(a.occupancies)),
        isComparator,
        isInMoreRoomLayer,
        proposal,
        currentComfortAlternatives,
      ),
      servicesIconsList: getServicesIconsList(),
      isBestFamilyDeal:
        (a.familyRoom || a.connectingRoom) && (proposal?.attendees?.children?.length || 0) > 0,
      isCabin: product.isCruise,
      isPremium,
      isExclusiveCollectionRoom: isExclusiveCollection,
      isExclusiveCollectionSpace: isExclusiveCollectionSpace(a, product),
      outsideFacilities: a.outsideFacilities,
      quantity: a.quantity,
      services: services?.[0],
      stock: remainingStock ? remainingStock : proposal?.remainingStock || 0,
      streetView: (matchingStreetViewElement as StreetViewWithAccommodationCategoryId | undefined)
        ?.streetView,
      type: a.roomType,
    };
  });
};

export const getAccommodationProps = ({
  accommodations = [],
  accommodationsCount,
  attendeesWithDistributionData,
  proposal = asProposalFixture({}),
  product,
  remainingStock,
  streetViews,
  isComparator = false,
  isInMoreRoomLayer = false,
  isUpgradableOnline = true,
  alternatives,
}: {
  accommodations: Array<Accommodation>;
  accommodationsCount: number | null | undefined;
  attendeesWithDistributionData: Array<any>;
  proposal: Proposal | null | undefined;
  product: Product;
  remainingStock: number | null | undefined;
  streetViews?: Array<StreetViewWithAccommodationCategoryId>;
  isComparator?: boolean;
  isInMoreRoomLayer?: boolean;
  isUpgradableOnline?: boolean;
  alternatives?: any;
}): Partial<Accommodationprops> => {
  const rooms = getAccommodationRoomsProps({
    accommodations,
    accommodationsCount,
    attendeesWithDistributionData,
    proposal,
    remainingStock,
    product,
    streetViews,
    isComparator,
    isInMoreRoomLayer,
    alternatives,
  });
  return {
    hasFlexRates: proposal?.price?.hasFlexRates,
    isCabin: product.isCruise,
    isUpgradableOnline,
    proposal,
    rooms,
    stock: remainingStock ? remainingStock : proposal?.remainingStock || 0,
    streetViews: streetViews || [],
  };
};
export {
  aggregateIconList,
  getBabyBedIconItem,
  getCapacityIconItem,
  getSurfaceIconItem,
  getOutsideFacilitiesIconItem,
  getViewIconItem,
  getNbBabies,
  hasLowStock,
  getAccommodationCategoryComfortIndex,
  getBestAccommodationCategory,
};
